import { Component, inject, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CredentialType } from 'enums/CredentialType';
import { InstallmentPaymentModel } from 'models/generated/Entities.Accounting.InstallmentPayment';
import { SelectListItemModel } from 'models/SelectList';
import { Inputs } from 'modules/inputs';
import { Layout } from 'modules/layout';
import { ListView } from 'modules/list-view';
import { Pipes } from 'modules/pipes';
import { PaymentIntervalPipe } from 'pipes/payment-interval.pipe';
import { AccountingService } from 'services/accounting.service';
import { CredentialRequired } from 'services/credential.service';
import { DateService } from 'services/date.service';
import { NavigationService } from 'services/navigation.service';
import { InputType } from 'enums/InputType';

@CredentialRequired(CredentialType.EditInvoiceInstallmentPayments)
@Component({
	templateUrl: './edit-invoice-payments.component.html',
	imports: [Layout, FormsModule, Inputs, ListView, Pipes]
})
export class EditInvoicePaymentsComponent {
	@Input() documentNo?: string;
	@Input() brandId?: string;
	@Input() set remainingAmount(amount: number) {
		this._remainingAmount = amount;
		this.calculatePayments(false);
	}
	@Input() set existingPayments(payments: InstallmentPaymentModel[]) {
		this._existingPayments = [...payments].sort((a, b) => this.dateService.getDiffInDays(a.dueDate!, b.dueDate!));
		if (this._existingPayments.length > 0) {
			const remainingPayments = this._existingPayments.filter(p => !p.paid);
			this.numberOfRemainingInstallments = remainingPayments.length;
			if (remainingPayments[0]?.dueDate)
				this.nextDueDate = remainingPayments[0].dueDate;
			if (remainingPayments.length > 1)
				this.interval = this.dateService.getDiffInMonths(remainingPayments[0].dueDate!, remainingPayments[1].dueDate!);
		}
		this.calculatePayments(false);
	}
	get onlyUnpaidInstallments() {
		if (!this._existingPayments || this._existingPayments.length == 0) return false;
		if (this._existingPayments.find(p => p.paid)) return false;
		return true;
	}

	private readonly accountingService = inject(AccountingService);
	private readonly navigationService = inject(NavigationService);
	private readonly dateService = inject(DateService);

	inputType = InputType;
	payments: InstallmentPaymentModel[] = [];
	today = this.dateService.today();
	nextDueDate = this.today;
	numberOfRemainingInstallments = 2;
	interval = 1;

	installmentNumbers: SelectListItemModel[];
	paymentIntervals: SelectListItemModel[];

	private _existingPayments?: InstallmentPaymentModel[];
	private _remainingAmount?: number;

	constructor(paymentIntervalPipe: PaymentIntervalPipe) {
		this.installmentNumbers = [...Array(23).keys()].map(i => new SelectListItemModel(i + 2, (i + 2).toString()));
		this.paymentIntervals = [1, 3, 6].map(i => new SelectListItemModel(i, paymentIntervalPipe.transform(i)));
	}

	calculatePayments(clearCurrentInstallments = true) {
		if (!this._remainingAmount || !this._existingPayments || !this.numberOfRemainingInstallments || !this.interval) return;

		if ((!clearCurrentInstallments && this._existingPayments.length > 0)) {
			this.payments = this._existingPayments;
			return;
		}

		const payments: InstallmentPaymentModel[] = [];
		for (const payment of this._existingPayments.filter(p => p.paid)) {
			payments.push(payment);
		}
		
		const splitPayment = Math.ceil(this._remainingAmount / this.numberOfRemainingInstallments);
		const remainderPayment = this._remainingAmount - (splitPayment * (this.numberOfRemainingInstallments - 1));
		for (let i = 0; i < this.numberOfRemainingInstallments; i++) {
			const dueDate = this.dateService.addMonths(this.nextDueDate, i * this.interval);
			const amount = i == (this.numberOfRemainingInstallments - 1) ? remainderPayment : splitPayment;
			payments.push(Object.assign(new InstallmentPaymentModel(), { amount: amount, paid: false, dueDate: dueDate }));
		}
		this.payments = payments;
	}

	getStatusColor(payment: InstallmentPaymentModel): string {
		if (payment.paid) return 'color-green-50';
		if (payment.dueDate && new Date(payment.dueDate) < new Date()) return 'color-red-50';
		return 'color-gray-40';
	}

	validPayments(): boolean {
		return this.payments.every(p => p.amount > 0 && p.dueDate) &&
			this.differenceBetweenPaymentsAndRemainingAmount == 0;
	}

	get validPaymentsStatus(): string {
		return this.differenceBetweenPaymentsAndRemainingAmount > 0 ? 'invoice.installment-payments.invalid-too-much' :
			this.differenceBetweenPaymentsAndRemainingAmount < 0 ? 'invoice.installment-payments.invalid-too-little' :
			'invoice.installment-payments.valid';
	}

	get differenceBetweenPaymentsAndRemainingAmount(): number {
		return this.payments.filter(p => !p.paid).reduce((sum, payment) => sum + payment.amount, 0) - (this._remainingAmount ?? 0);
	}

	copyDown(payment: InstallmentPaymentModel, paymentOrder: InstallmentPaymentModel[]) {
		for (let i = paymentOrder.indexOf(payment); i < paymentOrder.length; i++) {
			paymentOrder[i].amount = payment.amount;
		}
		paymentOrder[paymentOrder.length - 1].amount -= this.differenceBetweenPaymentsAndRemainingAmount;
		if (paymentOrder[paymentOrder.length - 1].amount < 0) paymentOrder[paymentOrder.length - 1].amount = 0;
	}

	async saveUpcomingPayments() {
		if (!this.documentNo || !this.brandId) return;
		for (const payment of this.payments) { // Ensure correct brand ID and document number on all payments, or they won't be saved correctly
			payment.documentNo = this.documentNo;
			payment.brandId = this.brandId;
		}
		await this.accountingService.setRemainingInstallmentPayments(this.brandId, this.documentNo, this.payments.filter(p => !p.paid));
		this.close();
	}
	async cancelAllPayments() {
		if (!this.documentNo || !this.brandId) return;
		await this.accountingService.setRemainingInstallmentPayments(this.brandId, this.documentNo, []);
		this.close();
	}
	close() {
		this.navigationService.closeView(this);
	}
}
